import { ExtranetOrderStatusEnum, MultilingualKeyDto } from '@pharmac-extranet/extranet-api'
import {
  ControlledCheckbox,
  ControlledCheckboxes,
  ControlledDateRangePicker,
  ControlledInput,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacButton } from '@pharmac/ui'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import {
  ExtranetFiltersColumn,
  ExtranetFiltersFooter,
  ExtranetFiltersRow,
} from '../../components/filters/ExtranetFiltersComponents'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import { GetOrderHistoryParams } from '../../views/order-history/OrderHistoryView'
import { mapOrderSearchParamsToQueryParams } from './util/query-parameter-mapping'

type Props = {
  closeFilters: () => void
}

export const OrderHistoryFilters = ({ closeFilters }: Props) => {
  const { t } = useLanguage(['extranet', 'common'])
  const { minDesktop } = useBreakpoint()

  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState(mapOrderSearchParamsToQueryParams(searchParams))

  const formHook = useForm<GetOrderHistoryParams>({
    mode: 'onChange',
    defaultValues: {
      sendingDate: filters.sendingDate ?? '',
      endDate: filters.endDate ?? '',
      startDate: filters.startDate ?? '',
      vnr: filters.vnr ?? '',
      startCreatedDateRange: filters.startCreatedDateRange ?? '',
      endCreatedDateRange: filters.endCreatedDateRange ?? '',
      endSendingDate: filters.endSendingDate ?? '',
      startSendingDate: filters.startSendingDate ?? '',
      hasChanges: filters.hasChanges ?? undefined,
      status: filters.status ?? [],
    },
  })

  useEffect(() => {
    const params = mapOrderSearchParamsToQueryParams(searchParams)

    setFilters(params)

    formHook.reset({
      sendingDate: params.sendingDate ?? '',
      endDate: params.endDate ?? '',
      startDate: params.startDate ?? '',
      vnr: params.vnr ?? '',
      startCreatedDateRange: params.startCreatedDateRange ?? '',
      endCreatedDateRange: params.endCreatedDateRange ?? '',
      endSendingDate: params.endSendingDate ?? '',
      startSendingDate: params.startSendingDate ?? '',
      hasChanges: params.hasChanges ?? undefined,
      status: params.status ?? [],
    })
  }, [searchParams])

  const handleSubmit = (values: GetOrderHistoryParams) => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...filters,
          ...values,
        })
      )
    )
    closeFilters()
  }

  const orderStatus: Array<{
    value: ExtranetOrderStatusEnum
    name: MultilingualKeyDto
  }> = [
    {
      value: ExtranetOrderStatusEnum.Arrived,
      name: {
        fi: t('orderHistory.status.arrived'),
        en: t('orderHistory.status.arrived'),
        sv: t('orderHistory.status.arrived'),
      },
    },
    {
      value: ExtranetOrderStatusEnum.Cancelled,
      name: {
        fi: t('orderHistory.status.cancelled'),
        en: t('orderHistory.status.cancelled'),
        sv: t('orderHistory.status.cancelled'),
      },
    },
    {
      value: ExtranetOrderStatusEnum.InManufacturing,
      name: {
        fi: t('orderHistory.status.in-manufacturing'),
        en: t('orderHistory.status.in-manufacturing'),
        sv: t('orderHistory.status.in-manufacturing'),
      },
    },
    {
      value: ExtranetOrderStatusEnum.MissingInformation,
      name: {
        fi: t('orderHistory.status.missing-information'),
        en: t('orderHistory.status.missing-information'),
        sv: t('orderHistory.status.missing-information'),
      },
    },
    {
      value: ExtranetOrderStatusEnum.Sent,
      name: {
        fi: t('orderHistory.status.sent'),
        en: t('orderHistory.status.sent'),
        sv: t('orderHistory.status.sent'),
      },
    },
  ]

  return (
    <>
      <FormProvider {...formHook}>
        <ExtranetFiltersRow style={{ height: minDesktop ? '20vw' : undefined }}>
          <ExtranetFiltersColumn>
            <ControlledDateRangePicker
              control={formHook.control}
              label={t('orderHistory.orderDate')}
              startDateName="startCreatedDateRange"
              endDateName="endCreatedDateRange"
              startDateText={t('startDateText', { ns: 'common' })}
              endDateText={t('endDateText', { ns: 'common' })}
              placeholder={t('selectDateRange', { ns: 'common' })}
              weekText={t('week', { ns: 'common' })}
              calendarPlacement="bottom"
            />
          </ExtranetFiltersColumn>
          <ExtranetFiltersColumn>
            <ControlledDateRangePicker
              control={formHook.control}
              label={t('orderHistory.deliveryDate')}
              startDateName="startSendingDate"
              endDateName="endSendingDate"
              weekText={t('week', { ns: 'common' })}
              startDateText={t('startDateText', { ns: 'common' })}
              endDateText={t('endDateText', { ns: 'common' })}
              placeholder={t('selectDateRange', { ns: 'common' })}
              calendarPlacement="bottom"
            />
          </ExtranetFiltersColumn>
          <ExtranetFiltersColumn>
            <ControlledInput
              label={t('orderHistory.productNumber')}
              name="vnr"
              control={formHook.control}
              placeholder={t('extranet:orderHistory.vnrSearchPlaceholder')}
            />
          </ExtranetFiltersColumn>
          <ExtranetFiltersColumn>
            <ControlledCheckbox
              checkboxLabel={t('orderHistory.showOrderChanges')}
              name="hasChanges"
              control={formHook.control}
            />
          </ExtranetFiltersColumn>
          <ExtranetFiltersColumn>
            <ControlledCheckboxes
              column
              data={orderStatus}
              name="status"
              control={formHook.control}
              label={t('orderHistory.orderState')}
            />
          </ExtranetFiltersColumn>
        </ExtranetFiltersRow>
        <ExtranetFiltersFooter>
          <PharmacButton
            onClick={formHook.handleSubmit(handleSubmit, (a) => console.log(a))}
            disabled={!formHook.formState.isDirty}
          >
            {t('search', { ns: 'common' })}
          </PharmacButton>
        </ExtranetFiltersFooter>
      </FormProvider>
    </>
  )
}
